import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import makeStyles from '@mui/styles/makeStyles';
import { useQueryClient } from '@tanstack/react-query';
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import useFetchEventBookingOptions from 'src/app/components/features/event-booking-options/hooks/use-fetch-event-booking-options';
import TicketLinkImportTableFeature from 'src/app/components/features/ticket-link-imports/TicketLinkImportTableFeature';
import { useCreateTicketLinkImport } from 'src/app/hooks/ticket-link-imports/useCreateTicketLinkImport';
import { FETCH_TICKET_LINK_IMPORTS_QUERY_KEY } from 'src/app/hooks/ticket-link-imports/useFetchTicketLinkImports';
import Button from 'src/view/components/button/Button';
import { TabPanel } from 'src/view/components/tabs/TabPanel';

const useStyles = makeStyles((theme) => ({
    buttonsContainer: {
        paddingTop: theme.spacing(2),
        display: 'flex',
        justifyContent: 'flex-end',
    },
}));

export interface TicketLinkImportListFeatureProps {
    eventId: string;
    onSelectImport: (id: string, bookingOptionName: string) => void;
}

export default function TicketLinkImportListFeature(
    props: Readonly<TicketLinkImportListFeatureProps>
): ReactElement {
    const [activeTab, setActiveTab] = useState<string>('');

    const queryClient = useQueryClient();
    const classes = useStyles();

    const { data: bookingOptionsData, isLoading: bookingOptionsLoading } =
        useFetchEventBookingOptions(props.eventId);

    const { mutate: createTicketLinkImport, isLoading: createLoading } = useCreateTicketLinkImport(
        () => {
            queryClient.invalidateQueries({
                queryKey: [FETCH_TICKET_LINK_IMPORTS_QUERY_KEY],
                exact: false,
            });
        }
    );

    const availableBookingOptions = useMemo(() => {
        let data = bookingOptionsData?.data?.data || [];
        return [...data].sort(
            (a, b) => new Date(a.startDate).valueOf() - new Date(b.startDate).valueOf()
        );
    }, [bookingOptionsData]);

    useEffect(() => {
        if (availableBookingOptions.length > 0 && activeTab === '') {
            setActiveTab(availableBookingOptions[0].id);
        }
    }, [availableBookingOptions, activeTab]);

    const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

    const handleTabChange = (_: React.SyntheticEvent, value: string) => {
        setActiveTab(value);
    };

    const handleFileInput = (
        event: React.ChangeEvent<HTMLInputElement>,
        bookingOptionId: string
    ): void => {
        const file = event.target.files?.[0];
        if (file) {
            createTicketLinkImport({ bookingOptionId, file });
        }
    };

    return (
        <>
            {availableBookingOptions.length === 0 && bookingOptionsLoading && (
                <span>Loading...</span>
            )}

            {availableBookingOptions.length > 1 && (
                <Tabs
                    value={activeTab || false}
                    variant="scrollable"
                    sx={{ borderBottom: 1 }}
                    onChange={handleTabChange}
                >
                    {availableBookingOptions.map((bookingOption) => (
                        <Tab
                            key={bookingOption.id}
                            label={bookingOption.name}
                            value={bookingOption.id}
                        />
                    ))}
                </Tabs>
            )}

            {availableBookingOptions.map((bookingOption) => (
                <TabPanel
                    value={bookingOption.id}
                    activeTab={activeTab}
                    key={bookingOption.id}
                >
                    <TicketLinkImportTableFeature
                        onClickDetails={(id) =>
                            props.onSelectImport(id, bookingOption.name)
                        }
                        bookingOptionId={bookingOption.id}
                    />

                    <div className={classes.buttonsContainer}>
                        <Button
                            disabled={createLoading}
                            onClick={() => fileInputRefs.current[bookingOption.id]?.click()}
                        >
                            Create new import for {bookingOption.name}
                        </Button>
                    </div>

                    <input
                        type="file"
                        hidden
                        accept=".csv"
                        ref={(el) => {
                            fileInputRefs.current[bookingOption.id] = el;
                        }}
                        onChange={(e) => handleFileInput(e, bookingOption.id)}
                    />
                </TabPanel>
            ))}
        </>
    );
}